import * as React from 'react';
import { useState } from 'react';
import type { ToolProps } from './Tool';
import { Tool } from './Tool';
import { DEFAULT_PLACEMENT_CONFIG, reactNodeIsOfType } from '../../util';
import type { ChildrenProp } from '../common-types';
import { intl } from '../../lib';
import { FocusTrap } from 'focus-trap-react';
import { useLayer } from 'react-laag';

/**
 * Props for a {@link MenuTool}.
 */
export type MenuToolProps = Omit<ToolProps, 'onClick' | 'icon'> &
  Partial<Pick<ToolProps, 'icon'>> &
  Required<ChildrenProp>;

/**
 * A Tool (button) that shows a menu containing the given tools when clicking on it.
 * The tools are passed as children and the MenuTool will throw an exception when trying to display
 * an invalid child.
 *
 * @param properties {@link MenuToolProps} object
 * @return the created {@link React.JSX.Element}
 * @constructor
 */
export function MenuTool({
  icon = 'menu',
  children,
  containerElement,
  tooltipOptions = {
    // defaulted to immediately hide the tooltip without any delay
    // to prevent the tooltip from appearing above the shown menu when opening it
    delayLeave: 0
  },
  ...props
}: Readonly<
  MenuToolProps & {
    containerElement?: () => HTMLElement | null;
  }
>): React.JSX.Element {
  const [isOpen, setOpen] = useState(false);
  const { layerProps, triggerProps, renderLayer } = useLayer({
    isOpen: isOpen,
    onOutsideClick: () => setOpen(prev => !prev),
    container: containerElement?.() ?? undefined,
    ...DEFAULT_PLACEMENT_CONFIG
  });
  // hook config for the shown menu
  const layer = renderLayer(
    // determine how to render the layer

    <FocusTrap
      focusTrapOptions={{
        allowOutsideClick: true,
        escapeDeactivates: () => {
          setOpen(false);
          return true;
        },
        preventScroll: true,
        initialFocus: false
      }}
    >
      <div
        className="ehs-menu"
        {...layerProps}
        style={{ ...layerProps.style }}
        ref={layerProps.ref}
      >
        {React.Children.map(children, (child: React.ReactNode) => {
          if (
            !React.isValidElement<{
              onClick: (event: React.MouseEvent) => void;
              className: string;
              key: React.Key | null | undefined;
              icon: string | null | undefined;
              text: unknown;
              tooltip: unknown;
            }>(child)
          ) {
            return null;
          }

          // make sure the properties we need exist on the child
          if (!child.props.onClick) {
            // needs to be caught by a developer
            throw new Error(
              'MenuTool is expecting its children to have an onClick property.'
            );
          }

          // override tool click handlers so that they also close the menu
          const oldClickHandler = child.props.onClick;
          const newClickHandler = (event: React.MouseEvent): void => {
            setOpen(false);
            oldClickHandler(event);
          };

          // props that will be passed to the cloned element
          const clonedElementProps = {
            ...child.props,
            className: ['ehs-menu-child', child.props.className]
              .filter(Boolean)
              .join(' '),
            onClick: newClickHandler
          };

          // some special handling for if the child elements are `Tool`s
          if (reactNodeIsOfType(child, Tool)) {
            // use icon as fallback for the key, since the same icon shouldn't be repeated within the menu
            clonedElementProps.key = child.key ?? child.props.icon;
            // set the tool text to either use the defined 'text' or 'tooltip' property if one was passed
            clonedElementProps.text = child.props.text ?? child.props.tooltip;
            // remove the tooltip, since a tool inside a MenuTool having a tooltip doesn't really make sense
            clonedElementProps.tooltip = undefined;
          }

          // return adjusted element
          return React.cloneElement(child, clonedElementProps);
        })}
      </div>
    </FocusTrap>
  );
  return (
    <>
      {isOpen && layer}
      <Tool
        {...props}
        {...triggerProps}
        className={
          isOpen ? 'ehs-menuTool-trigger active' : 'ehs-menuTool-trigger'
        }
        icon={icon}
        onClick={isOpen ? () => setOpen(false) : () => setOpen(true)}
        tooltip={props.tooltip ?? intl('view.button.show_additional_tools')}
        tooltipOptions={tooltipOptions}
        aria-expanded={isOpen}
      />
    </>
  );
}
